import { useEffect, useMemo, useState } from "react";
import {
  App as AntdApp,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Space,
  Steps,
} from "antd";
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  SaveOutlined,
} from "@ant-design/icons";

import { controlPointService } from "../../../services/api";
import type { ControlPoint } from "../../../types/control-point";
import { ControlPointMapEditor } from "../../control-points/components/ControlPointMapEditor";

const { TextArea } = Input;

interface ControlPointReviewModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  auditId: number;
  controlPoint: ControlPoint | null;
}

interface ReviewFormValues {
  comment: string;
  estimated_surface?: number;
}

/**
 * Review wizard for control points precharged from a previous audit.
 *
 * Two steps, same shape as the plot review:
 *   1. Datos — review/update the CP info; the auditor types a new
 *              review comment and (optionally) refreshes the
 *              estimated surface for the audit.
 *   2. Mapa  — confirm or redraw the CP geometry on the field.
 *
 * "Finalizar revisión" posts the review (and a follow-up PATCH if
 * the geometry actually changed). "Dar de baja" lives on the row
 * action in the section — not inside this modal.
 */
export const ControlPointReviewModal: React.FC<ControlPointReviewModalProps> = ({
  visible,
  onClose,
  onSuccess,
  auditId,
  controlPoint,
}) => {
  const { message } = AntdApp.useApp();
  const [form] = Form.useForm<ReviewFormValues>();

  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [geometry, setGeometry] = useState<GeoJSON.Geometry | null>(null);

  useEffect(() => {
    if (visible && controlPoint) {
      setStep(0);
      setGeometry(controlPoint.geom ?? null);
      form.setFieldsValue({
        comment: controlPoint.latest_review?.comment ?? "",
        estimated_surface:
          controlPoint.latest_review?.estimated_surface ??
          controlPoint.total_estimated_surface ??
          0,
      });
    } else if (!visible) {
      form.resetFields();
    }
  }, [visible, controlPoint, form]);

  const submitReview = async () => {
    if (!controlPoint) return;
    let values: ReviewFormValues;
    try {
      values = await form.validateFields();
    } catch {
      setStep(0);
      return;
    }
    setSaving(true);
    try {
      await controlPointService.createReview({
        audit_id: auditId,
        control_point_id: controlPoint.id,
        comment: values.comment,
        estimated_surface: values.estimated_surface ?? 0,
      });
      // Geometry persists separately — only PATCH the CP when the
      // auditor actually redrew it on step 2.
      const initialGeom = JSON.stringify(controlPoint.geom ?? null);
      const newGeom = JSON.stringify(geometry ?? null);
      if (newGeom !== initialGeom) {
        try {
          await controlPointService.updateControlPoint(controlPoint.id, {
            geom: geometry ?? undefined,
          } as any);
        } catch {
          message.warning(
            "Revisión guardada, pero no se pudo actualizar la geometría.",
          );
        }
      }
      message.success("Revisión guardada");
      onSuccess();
    } catch (e: any) {
      message.error(e?.message || "No se pudo guardar la revisión");
    } finally {
      setSaving(false);
    }
  };

  // El controlPoint puede ser null al primer render del modal o
  // mientras se carga. Encapsulamos toda la lectura aquí para no
  // tener nunca un `if (!controlPoint) return null` antes de los
  // siguientes hooks — eso causaba React #310 ("Rendered more hooks
  // than during the previous render") al pasar de null → cargado.
  const stepDatos = useMemo(
    () => {
      if (!controlPoint) return null;
      return (
        <div>
          <Card
            title="Información actual"
            size="small"
            style={{ marginBottom: 16 }}
          >
            <Descriptions column={2} size="small">
              <Descriptions.Item label="Nombre">
                {controlPoint.name}
              </Descriptions.Item>
              <Descriptions.Item label="Motivo">
                {controlPoint.reason_name ?? "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Finca">
                {controlPoint.farm_name ?? "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Productor">
                {controlPoint.producer_name ?? "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Superficie estimada">
                {(controlPoint.total_estimated_surface ?? 0).toFixed(2)} ha
              </Descriptions.Item>
              <Descriptions.Item label="Revisiones">
                {controlPoint.review_count}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Form form={form} layout="vertical">
            <Form.Item
              label="Superficie estimada actualizada (ha)"
              name="estimated_surface"
              rules={[{ type: "number", min: 0 }]}
              help="Actualízala si la situación en campo ha cambiado."
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                step={0.01}
                precision={2}
                placeholder="0.00"
              />
            </Form.Item>

            <Form.Item
              label="Comentario de revisión"
              name="comment"
              rules={[{ required: true, message: "Indica un comentario" }]}
              help="Describe el estado actual del punto de control."
            >
              <TextArea
                rows={4}
                placeholder="Estado actual del punto de control…"
              />
            </Form.Item>
          </Form>
        </div>
      );
    },
    [controlPoint, form],
  );

  const stepMapa = (
    <div>
      <p style={{ color: "#8c8c8c", marginBottom: 12 }}>
        Revisa la ubicación del punto de control. Redibújala si ha
        cambiado.
      </p>
      <div style={{ height: 400 }}>
        <ControlPointMapEditor
          geometry={geometry}
          onGeometryChange={(g) => setGeometry(g)}
        />
      </div>
    </div>
  );

  const steps = [
    { key: "datos", title: "Datos", body: stepDatos },
    { key: "mapa", title: "Mapa", body: stepMapa },
  ];

  // Keep both step bodies mounted at the same time and toggle their
  // visibility with display:none so the <Form> never unmounts on
  // step navigation — otherwise antd drops the field registrations
  // and the submit POST arrives without the values.
  // Side-effect: Leaflet maps mount with zero size when hidden, so
  // we fire a resize event whenever the active step changes — the
  // map invalidates its size and re-fetches tiles.
  useEffect(() => {
    const id = window.setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
    }, 80);
    return () => window.clearTimeout(id);
  }, [step, visible]);

  const renderSteps = () => (
    <>
      {steps.map((s, idx) => (
        <div
          key={s.key}
          style={{ display: idx === step ? "block" : "none" }}
        >
          {s.body}
        </div>
      ))}
    </>
  );

  // Early return DESPUÉS de todos los hooks — la regla de oro de
  // React. Si todavía no tenemos un controlPoint cargado, no
  // renderizamos nada.
  if (!controlPoint) return null;

  return (
    <Modal
      title={`Revisión — ${controlPoint.name}`}
      open={visible}
      onCancel={() => (saving ? undefined : onClose())}
      width={800}
      destroyOnClose
      footer={
        <Space>
          <Button onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          {step > 0 && (
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => setStep((s) => s - 1)}
              disabled={saving}
            >
              Anterior
            </Button>
          )}
          {step < steps.length - 1 && (
            <Button
              type="primary"
              icon={<ArrowRightOutlined />}
              onClick={async () => {
                if (step === 0) {
                  try {
                    await form.validateFields();
                  } catch {
                    return;
                  }
                }
                setStep((s) => s + 1);
              }}
            >
              Siguiente
            </Button>
          )}
          {step === steps.length - 1 && (
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              onClick={submitReview}
            >
              Finalizar revisión
            </Button>
          )}
        </Space>
      }
    >
      <Steps current={step} style={{ marginBottom: 24 }}>
        {steps.map((s) => (
          <Steps.Step key={s.key} title={s.title} />
        ))}
      </Steps>
      {renderSteps()}
    </Modal>
  );
};

export default ControlPointReviewModal;
